/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multi-Model Image Studio Art Style Presets Grid (Task 10.4.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { useImageStudioStore, STYLE_PRESETS } from '../../stores/useImageStudioStore';
import { useI18nStore } from '../../stores/useI18nStore';
import { Palette, Check } from 'lucide-react';

export const StylePresetsGrid: React.FC = () => {
  const { t } = useI18nStore();
  const { stylePreset, setStylePreset } = useImageStudioStore();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-xs font-heading font-bold text-foreground flex items-center gap-1.5">
          <Palette className="w-4 h-4 text-primary" />
          <span>{t('media.selectStyleTitle')}</span>
        </label>
        <span className="text-[11px] text-foreground-muted">
          {t('media.stylePresetLabel')}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
        {STYLE_PRESETS.map((preset) => {
          const isSelected = stylePreset === preset.id;
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => setStylePreset(preset.id)}
              className={`group rounded-xl border overflow-hidden text-left flex flex-col transition-all duration-200 relative ${
                isSelected
                  ? 'border-primary ring-2 ring-primary/40 shadow-glow-primary/20 scale-[1.02]'
                  : 'border-surface-border hover:border-surface-border/80 opacity-80 hover:opacity-100'
              }`}
            >
              <div className="h-16 w-full relative overflow-hidden bg-surface-elevated">
                <img
                  src={preset.previewUrl}
                  alt={preset.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                {isSelected && (
                  <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-primary text-primary-fg flex items-center justify-center">
                    <Check className="w-2.5 h-2.5 stroke-[3]" />
                  </div>
                )}
              </div>
              <div className="p-2 bg-surface">
                <span className="text-[11px] font-bold text-foreground block truncate">
                  {preset.name}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
